import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  onCropComplete: (croppedImageBase64: string) => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
}

export function ImageCropDialog({
  open,
  onOpenChange,
  imageSrc,
  onCropComplete,
  isLoading = false,
  title = "Potong Foto Profil",
  description = "Atur posisi dan zoom untuk mendapatkan hasil yang sempurna",
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.5);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(0.5);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  const onCropChange = (crop: { x: number; y: number }) => {
    setCrop(crop);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropAreaComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropAndSave = async () => {
    if (!croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(croppedImage);
      onOpenChange(false);
      setCrop({ x: 0, y: 0 });
      setZoom(0.5);
      setCroppedAreaPixels(null);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative w-full bg-muted rounded-lg" style={{ height: "400px" }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid={true}
              onCropChange={onCropChange}
              onCropComplete={onCropAreaComplete}
              onZoomChange={onZoomChange}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Zoom: {zoom.toFixed(1)}x
            </label>
            <input
              type="range"
              min={0.5}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => onZoomChange(parseFloat(e.target.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button
            type="button"
            variant="outline"
            size="default"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Batal
          </Button>
          <Button
            type="button"
            variant="default"
            size="lg"
            className="w-full h-11 text-base font-semibold shadow-lg shadow-primary/20 ring-2 ring-primary/25 transition-shadow hover:shadow-xl hover:ring-primary/40 sm:w-auto sm:min-w-[13rem]"
            onClick={handleCropAndSave}
            disabled={isLoading || !croppedAreaPixels}
          >
            {isLoading ? "Menyimpan..." : "Simpan Foto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  const image = new Image();
  image.src = imageSrc;
  image.crossOrigin = "anonymous";

  return new Promise((resolve, reject) => {
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Could not get canvas context");
        }

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );

        const croppedImage = canvas.toDataURL("image/jpeg", 0.95);
        resolve(croppedImage);
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    image.onabort = () => {
      reject(new Error("Image loading was aborted"));
    };
  });
}

export interface PhotoCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageData: string;
  onCropComplete: (croppedImage: string) => void;
  aspectRatio?: number;
  title?: string;
  isLoading?: boolean;
}

export function PhotoCropDialog({
  open,
  onOpenChange,
  imageData,
  onCropComplete,
  title = "Crop Foto Profil",
  isLoading,
}: PhotoCropDialogProps) {
  return (
    <ImageCropDialog
      open={open}
      onOpenChange={onOpenChange}
      imageSrc={imageData}
      onCropComplete={onCropComplete}
      isLoading={isLoading}
      title={title}
    />
  );
}
